'use client';

import type { ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';

import { useEffect } from 'react';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';

import { layoutClasses } from 'src/layouts/core/classes';
import { ServiceLayout } from 'src/layouts/service/layout';

/**
 * **서비스 화면을 뒤에 까는 틀** — 왼쪽 메뉴 · 본문(계정 머리는 뺀다)을 그대로 깔고 화면에 붙은 밝은 막을 씌운다.
 * 공유 페이지 껍데기(`PublicShareShell`)와 만료 안내(`/expired`)가 같은 틀을 쓴다.
 *
 * 깔 화면은 `BackdropBehind` 로 감싸 `children` 에 넣고, 막 위에 설 것은 `BACKDROP_FRONT_Z` 층에 둔다.
 * 🔑 막 색은 바탕색(`background.paper` 채널)의 불투명도만 바꾼다 — 다크 모드에서는 같은 막이 어두운 바탕색이다.
 * ⚠️ 왼쪽 서비스 메뉴(워크스페이스 고르개 · 계정 칸 포함)는 눌리지 않고 키보드 초점도 못 들어간다(`inert` · `pointer-events: none`).
 * @param tint 화면 막의 색 — `a(불투명도)` 로 바탕색을 받아 CSS 배경값을 돌려준다
 * @param blurMask 화면 막의 흐림(8px)을 어디에 얼마나 걸지 — CSS 마스크 값
 * @param analytics 레이아웃이 진입 로그를 거는가 — 미리보기 판만 끈다
 */
export function ServiceBackdropShell({
  tint,
  blurMask,
  analytics = true,
  children,
}: {
  tint: BackdropTint;
  blurMask: string;
  analytics?: boolean;
  children: ReactNode;
}) {
  useInertNav();
  return (
    // 머리(계정·알림·워크스페이스)는 숨긴다 — 왼쪽 메뉴와 본문 틀만 남긴다
    <ServiceLayout analytics={analytics} slotProps={{ header: { sx: { display: 'none' } } }} sx={LOCKED_NAV_SX}>
      {/* 화면에 붙은 막 — 왼쪽 메뉴 위까지(메뉴보다 한 층 위 · 다이얼로그보다 아래) */}
      <BackdropVeil data-share-veil="screen" position="fixed" zIndex={BACKDROP_VEIL_Z} tint={tint} blurMask={blurMask} />
      {children}
    </ServiceLayout>
  );
}

/** 막 색 — `a(불투명도)` 가 바탕색(라이트 흰색 · 다크 어두운 면)을 준다 */
export type BackdropTint = (a: (x: number) => string) => string;

/** 막은 왼쪽 메뉴(`--layout-nav-zIndex`) 바로 위, 막 위에 서는 것은 그 위 — 다이얼로그(`modal` 1300)보다는 아래다 */
export const BACKDROP_VEIL_Z = 'calc(var(--layout-nav-zIndex) + 1)';
export const BACKDROP_FRONT_Z = 'calc(var(--layout-nav-zIndex) + 2)';

/** 막 한 벌 — 바탕색 그라데이션(색) 한 겹 + 마스크로 세기를 조절하는 흐림(최대 8px) 한 겹 */
export function BackdropVeil({
  position,
  zIndex,
  tint,
  blurMask,
  ...rest
}: {
  position: 'absolute' | 'fixed';
  zIndex: string;
  tint: BackdropTint;
  blurMask: string;
  [data: `data-${string}`]: string;
}) {
  return (
    <Box aria-hidden {...rest} sx={{ position, zIndex, inset: 0, pointerEvents: 'none' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          maskImage: blurMask,
          WebkitMaskImage: blurMask,
        }}
      />
      <Box sx={(theme) => ({ position: 'absolute', inset: 0, background: tint((x) => paper(x)(theme)) })} />
    </Box>
  );
}

/** 깔린 화면 — 아무것도 눌리지 않고 초점도 못 들어간다 · 읽기 도구에서도 빠진다 */
export function BackdropBehind({ pb, children }: { pb: number; children: ReactNode }) {
  return (
    <Box
      aria-hidden
      inert
      sx={{ pointerEvents: 'none', userSelect: 'none', width: '100%', maxWidth: 1680, mx: 'auto', px: { xs: 2, md: 2.5 }, pt: 1.5, pb }}
    >
      {children}
    </Box>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

const paper = (alpha: number) => (theme: Theme) => varAlpha(theme.vars?.palette.background.paperChannel ?? PAPER_FALLBACK, alpha);
/** 테마 변수가 없을 때(테마 밖 렌더) — 흰색 */
const PAPER_FALLBACK = '255 255 255';

/** 왼쪽 메뉴 — 눌리지 않고 손 모양 커서·호버 표시도 없다(바래는 것은 화면 막이 맡는다) */
const LOCKED_NAV_SX = { [`& .${layoutClasses.nav.root}`]: { pointerEvents: 'none', userSelect: 'none' } } as const;

/**
 * 왼쪽 메뉴를 `inert` 로 잠근다 — 키보드 초점이 못 들어간다. 메뉴는 레이아웃이 그려 이 틀 밖이라 DOM 에 직접 건다.
 * 🔑 메뉴가 다시 그려져도(접힘·폰 서랍) 새 마디에 다시 건다 · 떠나면 이 훅이 건 것만 뗀다.
 */
function useInertNav() {
  useEffect(() => {
    const locked = new Set<Element>();
    const apply = () => {
      document.querySelectorAll(`.${layoutClasses.nav.root}`).forEach((el) => {
        if (el.hasAttribute('inert')) return;
        el.setAttribute('inert', '');
        locked.add(el);
      });
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      locked.forEach((el) => el.removeAttribute('inert'));
    };
  }, []);
}
